import React, { useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiBarChart2,
  FiCheckCircle,
  FiClock,
  FiLink,
  FiRefreshCw,
  FiXCircle,
} from "react-icons/fi";
import {
  Card,
  Col,
  CardHeader,
  Row,
 
} from "reactstrap";
import api from "../../utills/api";
import BreadCrumb from "../../Components/Common/BreadCrumb";

const Google = () => {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");

  const [integration, setIntegration] = useState({
    connected: false,
    connectionStatus: "Not Connected",
    accountName: "",
    accountId: "",
    adsAccountId: "",
    googleUserEmail: "",
    lastSyncAt: null,
    lastError: "",
  });

  const loadIntegration = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await api.get("/google/status");
      const data = response?.data?.data || {};

      const saved =
        data?.integration || {};

      setIntegration({
        connected: Boolean(data?.connected),
        connectionStatus:
          saved?.connectionStatus ||
          "Not Connected",
        accountName:
          saved?.customerName ||
          "",
        accountId:
          saved?.googleUserId ||
          "",
        adsAccountId:
          saved?.customerId ||
          "",
        googleUserEmail:
          saved?.googleUserEmail ||
          "",
        lastSyncAt:
          saved?.lastSyncAt || null,
        lastError:
          saved?.lastError || "",
      });

      if (data?.connected && saved?.customerId) {
        setSelectedCustomer(
          String(saved.customerId)
        );
      }

      const params =
        new URLSearchParams(
          window.location.search
        );

      if (params.get("connected") === "true") {
        setMessage(
          params.get("youtube") === "connected"
            ? "Google connected successfully. YouTube was connected through the same Google OAuth."
            : "Google connected successfully."
        );

        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }

      if (params.get("error")) {
        setError(params.get("error"));
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }
    } catch (err) {
      console.error(
        "Google integration load failed:",
        err
      );

      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Unable to load Google integration."
      );
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response =
        await api.get(
          "/google/customers"
        );

      const resourceNames =
        response?.data?.data
          ?.resourceNames || [];

      setCustomers(
        resourceNames.map((value) => ({
          id: String(value)
            .replace(/^customers\//, ""),
          resourceName: value,
        }))
      );
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Unable to load Google Ads accounts."
      );
    }
  };

  useEffect(() => {
    loadIntegration();
  }, []);

  useEffect(() => {
    if (integration.connected) {
      loadCustomers();
    }
  }, [integration.connected]);

  const startOAuth = async () => {
    try {
      setActionLoading(true);
      setError("");
      setMessage("");

      const response = await api.get("/google/oauth/start");

      const authorizationUrl =
        response?.data?.data?.authorizationUrl;

      if (!authorizationUrl) {
        throw new Error(
          "Google authorization URL was not returned by the server."
        );
      }

      window.location.assign(authorizationUrl);
    } catch (err) {
      console.error("Google OAuth start error:", err);

      setActionLoading(false);

      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Unable to start Google OAuth."
      );
    }
  };

  const connectCustomer = async () => {
    if (!selectedCustomer) {
      setError(
        "Please select a Google Ads customer account first."
      );
      return;
    }

    try {
      setActionLoading(true);
      setError("");
      setMessage("");

      const customer =
        customers.find(
          (item) =>
            item.id === selectedCustomer
        );

      await api.post(
        "/google/connect",
        {
          customerId:
            selectedCustomer,
          customerName:
            customer?.id ||
            selectedCustomer,
        }
      );

      setMessage(
        "Google Ads customer account connected."
      );

      await loadIntegration();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Unable to connect the Google Ads customer."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const disconnect = async () => {
    try {
      setActionLoading(true);
      setError("");
      setMessage("");

      await api.post(
        "/google/disconnect"
      );

      setCustomers([]);
      setSelectedCustomer("");
      setMessage(
        "Google integration disconnected."
      );

      await loadIntegration();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Unable to disconnect Google."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const statusIcon = useMemo(() => {
    if (
      integration.connectionStatus ===
      "Connected"
    ) {
      return (
        <FiCheckCircle
          size={20}
          className="text-success"
        />
      );
    }

    if (
      integration.connectionStatus ===
      "Error"
    ) {
      return (
        <FiXCircle
          size={20}
          className="text-danger"
        />
      );
    }

    return (
      <FiClock
        size={20}
        className="text-warning"
      />
    );
  }, [integration.connectionStatus]);

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    return Number.isNaN(date.getTime())
      ? "—"
      : date.toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      });
  };

  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid py-5 text-center">
          <FiRefreshCw
            className="me-2"
            size={20}
          />
          Loading Google integration...
        </div>
      </div>
    );
  }

  document.title =
    "Google Intigration | eduLeads.Online";

  return (
    <div className="page-content">
      <div className="container-fluid py-3">
        <BreadCrumb
          title="Google Intigration"
          pageTitle="Intigration"
        />
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <div className="flex-grow-1">
                    <h5 className="mb-0">
                      Google Integration
                    </h5>

                    <small className="text-muted">
                      Connect Google once for Google Ads and YouTube.
                    </small>
                  </div>
                 <button
                  type="button"
                  className="btn btn-light"
                  onClick={loadIntegration}
                  disabled={actionLoading}
                >
                  <FiRefreshCw
                    className="me-1"
                    size={16}
                  />
                  Refresh
                </button>
                </div>
               
              </CardHeader>
            </Card>
          </Col>
        </Row>

        {error && (
          <div className="alert alert-warning d-flex align-items-start">
            <FiAlertCircle
              className="me-2 mt-1"
              size={18}
            />
            <div>{error}</div>
          </div>
        )}

        {message && (
          <div className="alert alert-success d-flex align-items-start">
            <FiCheckCircle
              className="me-2 mt-1"
              size={18}
            />
            <div>{message}</div>
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <div className="me-3 rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: 52, height: 52 }}>
                  <FiBarChart2 size={25} />
                </div>

                <div>
                  <h5 className="mb-1">
                    Google
                  </h5>

                  <div className="d-flex align-items-center gap-2">
                    {statusIcon}
                    <span>
                      {integration.connectionStatus}
                    </span>
                  </div>

                  {integration.googleUserEmail && (
                    <small className="text-muted">
                      {integration.googleUserEmail}
                    </small>
                  )}
                </div>
              </div>

              <div>
                {!integration.connected ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={startOAuth}
                    disabled={actionLoading}
                  >
                    <FiLink
                      size={15}
                      className="me-1"
                    />
                    {actionLoading
                      ? "Connecting..."
                      : "Connect Google"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={disconnect}
                    disabled={actionLoading}
                  >
                    Disconnect
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xl-6">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  Google Ads Account
                </h5>
              </div>

              <div className="card-body">
                {integration.connected ? (
                  <>
                    <label className="form-label">
                      Accessible Customer
                    </label>

                    <select
                      className="form-select mb-3"
                      value={selectedCustomer}
                      onChange={(e) =>
                        setSelectedCustomer(
                          e.target.value
                        )
                      }
                    >
                      <option value="">
                        Select Google Ads customer
                      </option>

                      {customers.map(
                        (customer) => (
                          <option
                            key={
                              customer.id
                            }
                            value={
                              customer.id
                            }
                          >
                            {customer.id}
                          </option>
                        )
                      )}
                    </select>

                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={
                        connectCustomer
                      }
                      disabled={
                        actionLoading ||
                        !selectedCustomer
                      }
                    >
                      Save Ads Account
                    </button>

                    {integration.adsAccountId && (
                      <div className="mt-4">
                        <small className="text-muted d-block">
                          Connected Customer ID
                        </small>
                        <strong>
                          {integration.adsAccountId}
                        </strong>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-muted mb-0">
                    Connect Google first to discover
                    the Google Ads accounts accessible
                    to this Google user.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="col-xl-6">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  Connection Details
                </h5>
              </div>

              <div className="card-body">
                <div className="mb-3">
                  <small className="text-muted d-block">
                    Google User
                  </small>
                  <strong>
                    {integration.googleUserEmail ||
                      "—"}
                  </strong>
                </div>

                <div className="mb-3">
                  <small className="text-muted d-block">
                    Google Ads Customer
                  </small>
                  <strong>
                    {integration.adsAccountId ||
                      "Not selected"}
                  </strong>
                </div>

                <div>
                  <small className="text-muted d-block">
                    Last Sync
                  </small>
                  <strong>
                    {formatDate(
                      integration.lastSyncAt
                    )}
                  </strong>
                </div>

                {integration.lastError && (
                  <div className="alert alert-danger mt-4 mb-0">
                    {integration.lastError}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="card mt-4">
          <div className="card-body">
            <h5 className="mb-2">
              Lead Capture
            </h5>
            <p className="text-muted mb-0">
              Google lead-form submissions are
              processed by the backend and stored
              as Education Leads with Google
              attribution and a dedicated Google
              submission ID.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Google;
